import { useState, useMemo } from "react";
import { Reveal, Overline } from "@/components/motion";
import { PRICING_CATEGORIES } from "@/data/pricing";
import { Search, Truck } from "lucide-react";

const Pricing = () => {
  const [active, setActive] = useState(PRICING_CATEGORIES[0].id);
  const [query, setQuery] = useState("");

  const category = PRICING_CATEGORIES.find((c) => c.id === active);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return category.items;
    return category.items.filter(
      (it) => it.item.toLowerCase().includes(q) || it.variant.toLowerCase().includes(q)
    );
  }, [category, query]);

  return (
    <>
      <section id="pricing" className="py-24 md:py-36 max-w-[1600px] mx-auto px-5 md:px-10" data-testid="pricing-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Intro */}
          <div className="lg:col-span-4">
            <Reveal><Overline>Transparent Pricing</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-4xl md:text-6xl font-light tracking-tight leading-none">
                No surprises.<br />Ever.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base font-light text-foreground/70 leading-relaxed max-w-sm">
                Indicative per-piece rates across {PRICING_CATEGORIES.length} categories. Final pricing is
                confirmed at pickup after inspection.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8 flex items-center gap-3 text-sm font-medium">
                <Truck size={18} className="text-foreground" />
                Min order ₹500 · Free pick &amp; drop · Ready in 48 hours
              </div>
            </Reveal>
          </div>

          {/* Catalog */}
          <div className="lg:col-span-8">
            {/* Category tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {PRICING_CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  data-testid={`pricing-tab-${c.id}`}
                  onClick={() => { setActive(c.id); setQuery(""); }}
                  className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                    active === c.id
                      ? "bg-foreground text-background border-foreground"
                      : "border-foreground/20 text-foreground/60 hover:border-foreground hover:text-foreground"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="flex items-center gap-3 border-b border-foreground/30 pb-2 mb-2">
              <Search size={16} className="text-foreground/40" />
              <input
                data-testid="pricing-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${category.label}…`}
                className="w-full bg-transparent outline-none text-base font-light placeholder:text-foreground/40"
              />
              <span className="text-xs text-foreground/40 whitespace-nowrap">{filtered.length} items</span>
            </div>

            {/* Items */}
            <div className="max-h-[560px] overflow-y-auto pr-1" data-testid="pricing-list">
              {filtered.length === 0 ? (
                <p className="py-10 text-foreground/50 font-light">No items match “{query}”.</p>
              ) : (
                filtered.map((p, i) => (
                  <div
                    key={`${p.item}-${p.variant}-${i}`}
                    data-testid="pricing-item"
                    className="flex items-baseline justify-between py-4 border-b border-foreground/12 group"
                  >
                    <div className="flex items-baseline gap-3 min-w-0">
                      <span className="font-sans text-base md:text-lg font-normal group-hover:translate-x-1 transition-transform duration-300">
                        {p.item}
                      </span>
                      <span className="text-sm font-light text-foreground/72 truncate">{p.variant}</span>
                    </div>
                    <span className="flex-1 mx-3 border-b border-dotted border-foreground/20 translate-y-[-4px] min-w-[16px]" />
                    <span className="font-serif text-lg md:text-xl whitespace-nowrap">{p.display}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Pricing;
